import type { ComponentProps, ReactNode } from 'react';

import { OverflowMenuVertical } from '@mint-lib/icons';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type ButtonProps = Omit<ComponentProps<typeof Button>, 'children'>;

type ActionMenuProps = Omit<ButtonProps, 'onClick'> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
};

/**
 * A row's kebab menu. The rest of the props land on the trigger button, where
 * the row's aria-label and Datadog name belong.
 */
export const ActionMenu = ({
  open,
  onOpenChange,
  className,
  children,
  ...triggerProps
}: ActionMenuProps) => (
  <Popover open={open} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild>
      <Button
        icon
        size="small"
        variant="plain"
        className={cn(
          'flex h-full min-w-6 flex-row items-center justify-center',
          className,
        )}
        onClick={() => onOpenChange(true)}
        // The row opens a folder on double click; the menu must not.
        onDoubleClick={(e) => e.stopPropagation()}
        {...triggerProps}
      >
        <OverflowMenuVertical />
      </Button>
    </PopoverTrigger>
    <PopoverPortal>
      <PopoverContent
        side="bottom"
        sideOffset={5}
        align="end"
        className="relative z-popover h-full w-max max-w-64 min-w-28.5 [scrollbar-width:none] overflow-y-auto scroll-auto rounded-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
      >
        {children}
      </PopoverContent>
    </PopoverPortal>
  </Popover>
);

type ActionMenuItemProps = ButtonProps & {
  startIcon: ReactNode;
  label: string;
  tone?: 'default' | 'danger';
};

export const ActionMenuItem = ({
  startIcon,
  label,
  tone = 'default',
  className,
  ...buttonProps
}: ActionMenuItemProps) => (
  <Button
    size="small"
    variant="text"
    className={cn(
      'h-10 w-full items-center justify-start gap-2 pl-3',
      className,
    )}
    onDoubleClick={(e) => e.stopPropagation()}
    {...buttonProps}
  >
    {startIcon}
    <p
      className={
        tone === 'danger'
          ? cn(
              typographyVariants({ variant: 'bodyShort01' }),
              'text-error-dark',
            )
          : typographyVariants({ variant: 'bodyShort01', color: 'primary' })
      }
    >
      {label}
    </p>
  </Button>
);
